import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X, User, Mail, Phone, MapPin, Calendar, Briefcase,
  Star, Award, Download, Upload,
  AlertCircle, Clock,
  Building, Target, Brain, Users,
  CheckCircle, XCircle, Edit3, Globe
} from 'lucide-react';
import { candidatesApi } from '../../services/api';
import api from '../../services/api';
import StepCard from './StepCard';
import AddStepModal from './AddStepModal';
import { useSofteamboardStore, normalizeStep } from '../../store/softeamboardStore';
import { useAuth } from '../../contexts/AuthContext';
import { useWebSocket } from '../../hooks/useWebSocket';
import { Modal } from '../shared/Modal';
import Toast from '../ui/Toast';

const normalizeCreatedStep = (step, fallbackDetails = {}) => {
  const normalized = normalizeStep(step);
  return {
    ...normalized,
    detailCandidateSent: normalized.detailCandidateSent || fallbackDetails.detailCandidateSent || null,
    detailCandidateInterview: normalized.detailCandidateInterview || fallbackDetails.detailCandidateInterview || null,
    detailGoNoGo: normalized.detailGoNoGo || fallbackDetails.detailGoNoGo || null,
    detailSale: normalized.detailSale || fallbackDetails.detailSale || null
  };
};

const CandidateDetailModal = ({ candidate, needId, open, onClose, onEdit, onDownloadCV, onCVReanalyzed }) => {
  // Initialize ALL hooks FIRST, before any early returns
  const [candidateData, setCandidateData] = useState(candidate);
  const [loading, setLoading] = useState(false);
  const [steps, setSteps] = useState([]);
  const [loadingSteps, setLoadingSteps] = useState(false);
  const [showAddStepModal, setShowAddStepModal] = useState(false);

  // CV re-analysis state
  const [analyzingCV, setAnalyzingCV] = useState(false);
  const [reanalysisJobId, setReanalysisJobId] = useState(null);
  const [reanalysisError, setReanalysisError] = useState(null);
  const cvFileInputRef = useRef(null);

  // UX feedback state
  const [toast, setToast] = useState(null);
  const [noGoModal, setNoGoModal] = useState({ open: false, reason: '' });

  // Add store functions
  const {
    getCandidate,
    getStepsForCandidate,
    loadStepsForCandidate,
    addStepToCache
  } = useSofteamboardStore();
  const cachedCandidate = getCandidate(candidate?.id);
  const { user } = useAuth();
  const isRecruiter = user?.role === 'Recruiter';

  // WebSocket handler for CV re-analysis completion
  const handleReanalysisWebSocket = useCallback((message) => {
    const messageType = message.type || message.event_type;
    const entityId = message.entity_id;

    if (!reanalysisJobId) return;

    const isRelevant = (
      messageType === 'candidate_analysis_complete' ||
      messageType === 'candidate_updated'
    ) && (entityId === reanalysisJobId || entityId === candidateData?.id);

    if (!isRelevant) return;

    console.log('CV re-analysis completed for candidate:', entityId);

    // Refresh candidate data
    (async () => {
      try {
        const refreshed = await candidatesApi.getCandidate(candidateData.id);
        setCandidateData(refreshed);
        onCVReanalyzed?.();
      } catch (err) {
        console.error('Failed to refresh candidate after re-analysis:', err);
      }
    })();

    setAnalyzingCV(false);
    setReanalysisJobId(null);
    setReanalysisError(null);
  }, [reanalysisJobId, candidateData?.id, onCVReanalyzed]);

  useWebSocket(
    localStorage.getItem('access_token'),
    analyzingCV ? handleReanalysisWebSocket : null,
    reanalysisJobId
  );

  // CV re-analysis handler
  const handleCVReanalysis = async (file) => {
    if (!file || !candidateData?.id) return;

    const allowedTypes = ['.pdf', '.doc', '.docx'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(ext)) {
      setReanalysisError('Please upload a PDF, DOC, or DOCX file');
      return;
    }

    try {
      setAnalyzingCV(true);
      setReanalysisError(null);

      const formData = new FormData();
      formData.append('files', file);
      formData.append('file_types', 'cv');
      formData.append('api_config_json', JSON.stringify({
        api_key: 'default',
        email: 'user@example.com',
        username: 'user',
        need_id: needId,
        candidate_id: candidateData.id
      }));

      const response = await api.post('/analyses-ia/file-analyses', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const analysisResponse = response.data;
      if (analysisResponse.results && analysisResponse.results.length > 0) {
        setReanalysisJobId(analysisResponse.results[0].entity_id);
      } else {
        throw new Error('No analysis results received');
      }
    } catch (error) {
      console.error('CV re-analysis failed:', error);
      setReanalysisError(error.message || 'CV re-analysis failed');
      setAnalyzingCV(false);
    }

    // Reset file input
    if (cvFileInputRef.current) cvFileInputRef.current.value = '';
  };

  // ✅ FIX: Add ref to track loaded steps to prevent infinite loops
  const loadedStepsRef = useRef(new Set());

  // ✅ FIX: Use the specific needId passed from ProcessTracker, don't fallback to first need
  const effectiveNeedId = needId;
  

  // Helper functions to get data from multiple sources
  const getSkills = () => {
    if (candidateData.cvAnalysis?.hardskills) {
      return candidateData.cvAnalysis.hardskills;
    }
    if (candidateData.cvHardSkillsJson) {
      try {
        return JSON.parse(candidateData.cvHardSkillsJson);
      } catch (e) {
        console.error('Error parsing cvHardSkillsJson:', e);
      }
    }
    return candidateData.skills || [];
  };

  const getSoftSkills = () => {
    if (candidateData.cvAnalysis?.softskills) {
      return candidateData.cvAnalysis.softskills;
    }
    if (candidateData.cvSoftSkillsJson) {
      try {
        return JSON.parse(candidateData.cvSoftSkillsJson);
      } catch (e) {
        console.error('Error parsing cvSoftSkillsJson:', e);
      }
    }
    return [];
  };

  const getKeyStrengths = () => {
    if (candidateData.cvAnalysis?.keystrengths) {
      return candidateData.cvAnalysis.keystrengths;
    }
    if (candidateData.cvKeyStrengthsJson) {
      try {
        return JSON.parse(candidateData.cvKeyStrengthsJson);
      } catch (e) {
        console.error('Error parsing cvKeyStrengthsJson:', e);
      }
    }
    return [];
  };

  const getLanguages = () => {
    if (candidateData.cvAnalysis?.languages) {
      return candidateData.cvAnalysis.languages;
    }
    if (candidateData.cvLanguagesJson) {
      try {
        return JSON.parse(candidateData.cvLanguagesJson);
      } catch (e) {
        console.error('Error parsing cvLanguagesJson:', e);
      }
    }
    return [];
  };

  const getMissingSkills = () => {
    if (candidateData.cvAnalysis?.missingskills) {
      return candidateData.cvAnalysis.missingskills;
    }
    if (candidateData.cvMissingSkillsJson) {
      try {
        return JSON.parse(candidateData.cvMissingSkillsJson);
      } catch (e) {
        console.error('Error parsing cvMissingSkillsJson:', e);
      }
    }
    return [];
  };

  // Fetch candidate data
  useEffect(() => {
    let current = true;
    if (open && candidate?.id) {
      setCandidateData(prev => ({ ...prev, ...candidate, ...cachedCandidate }));
      setLoading(true);
      candidatesApi.getCandidate(candidate.id)
        .then((fullCandidate) => {
          if (current) setCandidateData(fullCandidate);
        })
        .catch(() => { if (current) setCandidateData(cachedCandidate || candidate); })
        .finally(() => { if (current) setLoading(false); });
    }
    return () => { current = false; };
  }, [open, candidate?.id, candidate, cachedCandidate]);

  useEffect(() => {
    if (open && effectiveNeedId && candidate?.id) {
      setSteps(getStepsForCandidate(effectiveNeedId, candidate.id));
    }
  }, [open, effectiveNeedId, candidate?.id, getStepsForCandidate]);

  // Steps fetching - uses local ref to prevent re-fetching within same modal open
  useEffect(() => {
    if (!open || !effectiveNeedId || !candidate?.id) return;

    const cacheKey = `${effectiveNeedId}_${candidate.id}`;

    if (loadedStepsRef.current.has(cacheKey)) {
      setSteps(getStepsForCandidate(effectiveNeedId, candidate.id));
      return;
    }

    const cachedSteps = getStepsForCandidate(effectiveNeedId, candidate.id);
    if (cachedSteps.length > 0) {
      setSteps(cachedSteps);
      loadedStepsRef.current.add(cacheKey);
      return;
    }

    setLoadingSteps(true);
    loadStepsForCandidate(effectiveNeedId, candidate.id)
      .then(loadedSteps => {
        if (!loadedSteps) return;
        setSteps(loadedSteps);
        loadedStepsRef.current.add(cacheKey);
      })
      .catch(() => setSteps([]))
      .finally(() => setLoadingSteps(false));
  }, [open, effectiveNeedId, candidate?.id]); // Store functions excluded from deps intentionally

  // ✅ ADD: Clear loaded steps when modal closes
  useEffect(() => {
    if (!open) {
      loadedStepsRef.current.clear();
      setSteps([]);
    }
  }, [open]);


  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Update step handlers to refresh store cache
  const handleStepCreated = (newStep) => {
    setSteps(prev => [newStep, ...prev]);
    // ✅ ADD: Refresh store cache
    if (effectiveNeedId && candidate?.id) {
      loadStepsForCandidate(effectiveNeedId, candidate.id);
    }
  };

  const handleStepUpdated = (updatedStep) => {
    setSteps(prev => prev.map(step => 
      step.id === updatedStep.id ? updatedStep : step
    ));
    // ✅ ADD: Refresh store cache
    if (effectiveNeedId && candidate?.id) {
      loadStepsForCandidate(effectiveNeedId, candidate.id);
    }
  };

  const handleStepDeleted = (stepId) => {
    setSteps(prev => prev.filter(step => step.id !== stepId));
    // ✅ ADD: Refresh store cache
    if (effectiveNeedId && candidate?.id) {
      loadStepsForCandidate(effectiveNeedId, candidate.id);
    }
  };

  // Update handleQuickNoGo to refresh store cache
  const handleQuickNoGo = () => {
    if (!effectiveNeedId || !candidate.id) {
      console.error('Missing needId or candidate.id for No Go step');
      return;
    }
    setNoGoModal({ open: true, reason: '' });
  };

  const submitNoGo = async () => {
    try {
      setLoadingSteps(true);

      const stepData = {
        type: 'GO_NOGO',
        comment: noGoModal.reason || 'Candidate rejected',
        date: new Date().toISOString(),
        detailGoNoGo: {
          decision: 'NO_GO',
          projectedStartDate: null,
          tjm: null,
          commercialConcession: null,
          endDate: null
        }
      };

      const createdStep = await candidatesApi.createStep(effectiveNeedId, candidate.id, stepData);
      const normalizedStep = normalizeCreatedStep(createdStep, { detailGoNoGo: stepData.detailGoNoGo });

      setSteps(prev => [normalizedStep, ...prev]);
      addStepToCache(effectiveNeedId, candidate.id, normalizedStep);
      setToast({ message: 'No Go step created successfully', type: 'success' });
    } catch (error) {
      console.error('Error creating No Go step:', error);
      let errorMessage = 'Unknown error';

      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setToast({ message: `Failed to create No Go step: ${errorMessage}`, type: 'error' });
    } finally {
      setLoadingSteps(false);
      setNoGoModal({ open: false, reason: '' });
    }
  };

  // Early return AFTER all hooks are initialized
  if (!open || !candidateData) return null;

  const skills = getSkills();
  const softSkills = getSoftSkills();
  const keyStrengths = getKeyStrengths();
  const languages = getLanguages();
  const missingSkills = getMissingSkills();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4" role="dialog" aria-modal="true" aria-labelledby="candidate-detail-title">
      <div className="bg-white rounded-xl shadow-2xl w-full h-full sm:h-auto sm:max-w-6xl sm:max-h-[90vh] overflow-hidden">
        
        <div className="flex flex-col justify-between gap-4 border-b border-gray-200 bg-white p-4 sm:flex-row sm:items-center sm:px-6">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
              {candidateData.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <div>
              <h2 id="candidate-detail-title" className="text-xl font-bold text-gray-900 sm:text-2xl">{candidateData.name || 'Candidat sans nom'}</h2>
              {(candidateData.cvRole || candidateData.cv_role) && (
                <div className="mt-1 flex items-center text-sm font-medium text-blue-700 sm:text-base">
                  <Award size={16} className="mr-1" />
                  {candidateData.cvRole || candidateData.cv_role}
                </div>
              )}
              <div className="flex items-center space-x-4 mt-2 text-sm">
                {(candidateData.cvExperienceYears || candidateData.cv_experience_years) && (
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium flex items-center">
                    <Clock size={12} className="mr-1" />
                    {candidateData.cvExperienceYears || candidateData.cv_experience_years}
                  </span>
                )}
                {(candidateData.cvTrigramme || candidateData.cv_trigramme) && (
                  <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-xs font-medium font-mono">
                    {candidateData.cvTrigramme || candidateData.cv_trigramme}
                  </span>
                )}
                {(candidateData.cvCity || candidateData.cv_city || candidateData.cvCountry || candidateData.cv_country) && (
                  <span className="text-gray-600 flex items-center text-sm sm:text-base">
                    <MapPin size={14} className="mr-1" />
                    {[
                      candidateData.cvCity || candidateData.cv_city, 
                      candidateData.cvCountry || candidateData.cv_country
                    ].filter(Boolean).join(', ')}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            {!isRecruiter && candidateData.cvUrl && (
              <button
                onClick={onDownloadCV}
                className="flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
              >
                <Download size={14} className="mr-1" />
                <span className="hidden sm:inline">Télécharger le CV</span>
              </button>
            )}
            {!isRecruiter && (
              <>
                <input
                  ref={cvFileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files[0]) handleCVReanalysis(e.target.files[0]);
                  }}
                />
                <button
                  onClick={() => cvFileInputRef.current?.click()}
                  disabled={analyzingCV}
                  className={`flex items-center px-3 py-2 rounded-lg text-sm ${
                    analyzingCV
                      ? 'bg-purple-400 text-white cursor-wait'
                      : 'border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
                  }`}
                  title="Upload a new CV to re-analyze this candidate"
                >
                  {analyzingCV ? (
                    <Brain size={14} className="mr-1 animate-pulse" />
                  ) : (
                    <Upload size={14} className="mr-1" />
                  )}
                  <span className="hidden sm:inline">
                    {analyzingCV ? 'Analyse...' : 'Réanalyser'}
                  </span>
                </button>
              </>
            )}
            {!isRecruiter && (
              <button
                onClick={() => onEdit?.(candidateData)}
                className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                <Edit3 size={14} className="mr-1" />
                <span className="hidden sm:inline">Modifier</span>
              </button>
            )}
            <button onClick={onClose} className="min-h-[44px] min-w-[44px] rounded-full p-2 hover:bg-gray-100" aria-label="Fermer la fiche candidat">
              <X size={20} className="text-gray-500" />
            </button>
          </div>
          {reanalysisError && (
            <div className="mt-2 text-sm text-red-600 flex items-center">
              <AlertCircle size={14} className="mr-1" />
              {reanalysisError}
            </div>
          )}
        </div>

        {/* Mobile-Optimized Content */}
        <div className="overflow-y-auto max-h-[calc(100vh-200px)] sm:max-h-[calc(90vh-140px)]">
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600">Chargement...</span>
            </div>
          ) : (
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              
              {/* Steps History */}
              <div className="rounded-xl border border-blue-200 bg-blue-50">
                <div className="border-b border-blue-200 p-4 sm:px-5">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <Calendar className="w-5 h-5 mr-2" />
                      <span>Étapes de recrutement</span>
                      <span className="ml-2 text-xs font-normal text-gray-500">{steps.length} étape{steps.length > 1 ? 's' : ''}</span>
                    </h3>
                    <div className="flex items-center space-x-2">
                      {!isRecruiter && (
                        <>
                          <button
                            onClick={handleQuickNoGo}
                            disabled={loadingSteps}
                            className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
                            title="Quick reject candidate"
                          >
                            <XCircle className="w-4 h-4" />
                            <span>NO-GO</span>
                          </button>
                          <button
                            onClick={() => setShowAddStepModal(true)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                          >
                            <Calendar className="w-4 h-4" />
                            <span>Ajouter une étape</span>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="p-4 sm:p-5">
                  {loadingSteps ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-gray-600">Chargement des étapes...</span>
                    </div>
                  ) : steps.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Aucune étape enregistrée</p>
                      <p className="text-sm">Ajoutez la première étape pour démarrer le suivi.</p>
                    </div>
                  ) : (
                    <div className="space-y-4 max-h-64 sm:max-h-96 overflow-y-auto">
                      {steps.map((step) => (
                        <StepCard
                          key={step.id}
                          step={step}
                          needId={effectiveNeedId}
                          candidateId={candidate.id}
                          readOnly={isRecruiter}
                          onStepUpdated={handleStepUpdated}
                          onStepDeleted={handleStepDeleted}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Brain className="mr-2" size={18} />
                  Synthèse professionnelle
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                    <div className="text-2xl font-bold text-blue-600">
                      {candidateData.cvExperienceYears || candidateData.cv_experience_years || 'N/A'}
                    </div>
                    <div className="text-sm text-gray-600">Expérience</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                    <div className="text-2xl font-bold text-green-600">
                      {candidateData.cvTrigramme || candidateData.cv_trigramme || 'N/A'}
                    </div>
                    <div className="text-sm text-gray-600">Trigramme</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                    <div className="text-lg font-bold text-purple-600">
                      {(candidateData.cvAvailability || candidateData.cv_availability) && 
                       (candidateData.cvAvailability !== 'Non spécifiée' && candidateData.cv_availability !== 'Non spécifiée') 
                        ? (candidateData.cvAvailability || candidateData.cv_availability)
                        : 'Non spécifiée'}
                    </div>
                    <div className="text-sm text-gray-600">Disponibilité</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                    <div className="text-lg font-bold text-orange-600">
                      {skills.length}
                    </div>
                    <div className="text-sm text-gray-600">Compétences</div>
                  </div>
                </div>
              </div>

              {/* Skills Overview - Enhanced */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Technical Skills - Mobile Optimized */}
                {skills.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                      <Award className="mr-2" size={18} />
                      Compétences techniques ({skills.length})
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {skills.map((skill, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Soft Skills */}
                {softSkills.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                      <Users className="mr-2" size={18} />
                      Compétences relationnelles ({softSkills.length})
                    </h3>
                    <div className="space-y-2">
                      {softSkills.map((skill, index) => (
                        <div key={index} className="flex items-start">
                          <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                          <span className="text-sm text-gray-700">{skill}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Key Strengths - Enhanced */}
              {keyStrengths.length > 0 && (
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <Star className="mr-2" size={18} />
                    Points forts ({keyStrengths.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {keyStrengths.map((strength, index) => (
                      <div key={index} className="flex items-start">
                        <CheckCircle size={16} className="text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{strength}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Missing Skills - New Section */}
              {missingSkills.length > 0 && (
                <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <Target className="mr-2" size={18} />
                    Points à approfondir ({missingSkills.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {missingSkills.map((skill, index) => (
                      <div key={index} className="flex items-start">
                        <AlertCircle size={16} className="text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{skill}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Professional Experience - Enhanced */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <Briefcase className="mr-2" size={18} />
                  Expériences professionnelles
                </h3>
                <div className="space-y-4">
                  {/* First Experience */}
                  {(candidateData.cv1stExperienceTitle || candidateData.cv_1st_experience_title) && (
                    <div className="border-l-4 border-blue-500 pl-4 bg-white p-4 rounded-lg shadow-sm">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-2 gap-2">
                        <div>
                          <h4 className="font-semibold text-gray-900 text-lg">
                            {candidateData.cv1stExperienceTitle || candidateData.cv_1st_experience_title}
                          </h4>
                          <p className="text-blue-600 font-medium">
                            {candidateData.cv1stExperienceCompany || candidateData.cv_1st_experience_company}
                          </p>
                          <p className="text-gray-500 text-sm break-words">
                            {candidateData.cv1stExperienceStartDate || candidateData.cv_1st_experience_start_date} - {candidateData.cv1stExperienceEndDate || candidateData.cv_1st_experience_end_date}
                            {(candidateData.cv1stExperienceLocation || candidateData.cv_1st_experience_location) && 
                             ` • ${candidateData.cv1stExperienceLocation || candidateData.cv_1st_experience_location}`}
                          </p>
                        </div>
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                          Dernière expérience
                        </span>
                      </div>
                      {(candidateData.cv1stExperienceResponsibilitiesJson || candidateData.cv_1st_experience_responsibilities_json) && (
                        <div className="mt-3">
                          <div className="text-sm font-medium text-gray-700 mb-2">Key Responsibilities:</div>
                          <ul className="text-sm text-gray-600 space-y-1">
                            {(() => {
                              try {
                                const responsibilities = JSON.parse(candidateData.cv1stExperienceResponsibilitiesJson || candidateData.cv_1st_experience_responsibilities_json || '[]');
                                return responsibilities.map((resp, index) => (
                                  <li key={index} className="flex items-start">
                                    <span className="w-1 h-1 bg-gray-400 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                                    {resp}
                                  </li>
                                ));
                              } catch (e) {
                                return null;
                              }
                            })()}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Second Experience */}
                  {(candidateData.cv2ndExperienceTitle || candidateData.cv_2nd_experience_title) && 
                   (candidateData.cv2ndExperienceTitle !== 'Non spécifié' && candidateData.cv_2nd_experience_title !== 'Non spécifié') && (
                    <div className="border-l-4 border-gray-400 pl-4 bg-white p-4 rounded-lg shadow-sm">
                      <h4 className="font-semibold text-gray-900">
                        {candidateData.cv2ndExperienceTitle || candidateData.cv_2nd_experience_title}
                      </h4>
                      <p className="text-blue-600 font-medium">
                        {candidateData.cv2ndExperienceCompany || candidateData.cv_2nd_experience_company}
                      </p>
                      <p className="text-gray-500 text-sm break-words">
                        {candidateData.cv2ndExperienceStartDate || candidateData.cv_2nd_experience_start_date} - {candidateData.cv2ndExperienceEndDate || candidateData.cv_2nd_experience_end_date}
                        {(candidateData.cv2ndExperienceLocation || candidateData.cv_2nd_experience_location) && 
                         (candidateData.cv2ndExperienceLocation !== 'Non spécifié' && candidateData.cv_2nd_experience_location !== 'Non spécifié') &&
                         ` • ${candidateData.cv2ndExperienceLocation || candidateData.cv_2nd_experience_location}`}
                      </p>
                    </div>
                  )}

                  {/* Third Experience */}
                  {(candidateData.cv3rdExperienceTitle || candidateData.cv_3rd_experience_title) && 
                   (candidateData.cv3rdExperienceTitle !== 'Non spécifié' && candidateData.cv_3rd_experience_title !== 'Non spécifié') && (
                    <div className="border-l-4 border-gray-300 pl-4 bg-white p-4 rounded-lg shadow-sm">
                      <h4 className="font-semibold text-gray-900">
                        {candidateData.cv3rdExperienceTitle || candidateData.cv_3rd_experience_title}
                      </h4>
                      <p className="text-blue-600 font-medium">
                        {candidateData.cv3rdExperienceCompany || candidateData.cv_3rd_experience_company}
                      </p>
                      <p className="text-gray-500 text-sm break-words">
                        {candidateData.cv3rdExperienceStartDate || candidateData.cv_3rd_experience_start_date} - {candidateData.cv3rdExperienceEndDate || candidateData.cv_3rd_experience_end_date}
                        {(candidateData.cv3rdExperienceLocation || candidateData.cv_3rd_experience_location) && 
                         (candidateData.cv3rdExperienceLocation !== 'Non spécifié' && candidateData.cv_3rd_experience_location !== 'Non spécifié') &&
                         ` • ${candidateData.cv3rdExperienceLocation || candidateData.cv_3rd_experience_location}`}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Languages */}
              {languages.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <Globe className="mr-2" size={18} />
                    Langues ({languages.length})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {languages.map((language, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm"
                      >
                        {language}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact & Location */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <User className="mr-2" size={18} />
                    Coordonnées
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center">
                      <Mail className="mr-3 text-gray-400" size={16} />
                      <span className="break-all">{candidateData.email || 'Non renseigné'}</span>
                    </div>
                    <div className="flex items-center">
                      <Phone className="mr-3 text-gray-400" size={16} />
                      <span>{candidateData.phone || 'Non renseigné'}</span>
                    </div>
                    <div className="flex items-center">
                      <MapPin className="mr-3 text-gray-400" size={16} />
                      <span className="break-words">
                        {[
                          candidateData.cvCity || candidateData.cv_city, 
                          candidateData.cvPostalCode || candidateData.cv_postal_code, 
                          candidateData.cvCountry || candidateData.cv_country
                        ]
                          .filter(Boolean)
                          .join(', ') || 'Non renseignée'}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <Calendar className="mr-3 text-gray-400" size={16} />
                      <span>Ajouté le {formatDate(candidateData.createdAt)}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <Clock className="mr-2" size={18} />
                    Statut et disponibilité
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <span className="text-gray-600">Disponibilité :</span>
                      <span className="font-medium">
                        {(candidateData.cvAvailability || candidateData.cv_availability) && 
                         (candidateData.cvAvailability !== 'Non spécifiée' && candidateData.cv_availability !== 'Non spécifiée') 
                          ? (candidateData.cvAvailability || candidateData.cv_availability)
                          : 'Non spécifiée'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Statut du CV :</span>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        candidateData.cvAnalysisStatus === 'READY' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {candidateData.cvAnalysisStatus || 'Not analyzed'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Dernière mise à jour :</span>
                      <span className="font-medium">{formatDate(candidateData.updatedAt)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Associated Needs */}
              {candidateData.needs && candidateData.needs.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <Building className="mr-2" size={18} />
                    Besoins associés ({candidateData.needs.length})
                  </h3>
                  <div className="space-y-3">
                    {candidateData.needs.map((need, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-white rounded border shadow-sm">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 text-sm">{need.title}</h4>
                          <p className="text-gray-600 text-xs">{need.client}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-800">
                            {need.priority}
                          </span>
                          <span className="text-xs text-gray-500 hidden sm:inline">{need.column}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add Step Modal - Mobile Optimized */}
      {!isRecruiter && showAddStepModal && (
        <AddStepModal
          needId={effectiveNeedId}
          candidateId={candidate.id}
          open={showAddStepModal}
          onClose={() => setShowAddStepModal(false)}
          onStepCreated={handleStepCreated}
        />
      )}

      {/* No Go Reason Modal */}
      <Modal
        open={noGoModal.open}
        onClose={() => setNoGoModal({ open: false, reason: '' })}
        title="Reject Candidate"
        maxWidth="max-w-md"
        headerGradient="from-red-50 to-orange-50"
      >
        <div className="p-6">
          <label htmlFor="nogo-reason" className="block text-sm font-medium text-gray-700 mb-2">
            Reason for rejection (optional)
          </label>
          <textarea
            id="nogo-reason"
            value={noGoModal.reason}
            onChange={(e) => setNoGoModal(prev => ({ ...prev, reason: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows="3"
            placeholder="Enter reason..."
          />
          <div className="flex justify-end space-x-3 mt-4">
            <button
              type="button"
              onClick={() => setNoGoModal({ open: false, reason: '' })}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submitNoGo}
              disabled={loadingSteps}
              className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors font-medium disabled:opacity-50"
            >
              {loadingSteps ? 'Creating...' : 'Confirm No Go'}
            </button>
          </div>
        </div>
      </Modal>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default React.memo(CandidateDetailModal);
